/* ============================================================
   sc-film-room.js
   The Film Room -- rendering logic, filters, routing, modal.

   Data is injected by sections/sc-film-room.liquid from the
   "Film" metaobjects (Shopify admin > Content > Metaobjects > Film),
   250 films per page (Shopify's limit); loadFilms() below fetches
   any further pages before boot.
   Each film entry carries: id, handle, title, desc, vimeo (link),
   cat, range, type, orient, sound, tone, stage, fabric, durS, poster,
   and isNew (the "Show in What's New" tick box).

   Depends on: the section's data-sc-film-data JSON block, plus
   SC.filmRoom.COUNTRY and SC.filmRoom.CMS (set inline by the section)
   ============================================================ */
(function(global) {
  'use strict';
  var SC = global.SC = global.SC || {};
  SC.filmRoom = SC.filmRoom || {};
  var DATA = SC.filmRoom.DATA || [];

  /* Ensure DATA is set even if load order varies */
  Object.defineProperty(SC.filmRoom, 'DATA', {
    set: function(v) { DATA = v; },
    get: function() { return DATA; },
    configurable: true
  });


/* ------- config ------- */
var DEFAULT_FEATURED_IDS = ["video_312","video_314","video_62","video_369"];
/* Theme editor override (set by an inline script in sc-film-room.liquid,
   before this file loads). If the merchant has picked specific films in the
   theme editor, those win; otherwise fall back to the curated defaults above.
   Entries may be a film's handle or its legacy ID. */
var _cms = (SC.filmRoom && SC.filmRoom.CMS) || {};
var FEATURED_IDS = (_cms.featuredIds && _cms.featuredIds.length) ? _cms.featuredIds : DEFAULT_FEATURED_IDS;
var FEATURED_IMAGES = _cms.featuredImages || [];
var FEATURED_BLURB = {
  "video_13":"Sophie tells her story: growing up with Terence Conran, Habitat, and the house at Barton Court that shaped everything she makes today.",
};
var PAT = {
  "Sofas & Furniture":"pat-sofas",
  "Beds & Mattresses":"pat-beds",
  "Tableware & Candles":"pat-tableware",
  "Brand & Craftsmanship":"pat-brand",
  "Home & Lifestyle":"pat-home",
  "Bed Linen":"pat-linen",
  "Recipes & Cooking":"pat-recipes",
  "Gardening":"pat-garden"
};
var CAT_LABEL = {
  "Sofas & Furniture":"Sofas & Furniture",
  "Beds & Mattresses":"Beds & Mattresses",
  "Tableware & Candles":"Tableware & Candles",
  "Brand & Craftsmanship":"Brand & Craftsmanship",
  "Home & Lifestyle":"Home & Lifestyle",
  "Bed Linen":"Bed Linen",
  "Recipes & Cooking":"Recipes & Cooking",
  "Gardening":"Gardening"
};

/* Collection pages: each with its own film-title heading */
var COLLECTIONS = [
  {slug:"coco", title:"Some Like It Soft", eyebrow:"The Coco Collection", pat:"pat-sofas",
   lede:"Our original sofa. Every film about the Coco, from cushion fillings and washable covers to corner configurations and split frames.",
   match:v=>["Coco","Coco Corner"].includes(v.range)},
  {slug:"workshop", title:"Once Upon a Time in the Workshop", eyebrow:"Craft & Making", pat:"pat-brand",
   lede:"Behind the scenes in Derbyshire and North Carolina, where every piece is made by hand, from frame to final stitch.",
   match:v=>v.cat==="Brand & Craftsmanship"||v.type==="Craftsmanship & BTS"},
  {slug:"fabric", title:"Gone with the Weave", eyebrow:"Fabrics & Colourways", pat:"pat-linen",
   lede:"Mills, weaves and colourways. The fabrics that dress every sofa, and the people who make them.",
   match:v=>v.range==="Fabric"||/\b(mill|weave|weaving|woven|loom)\b/i.test(v.title+" "+v.desc)},
  {slug:"cocobella", title:"The Devil Wears Petunia", eyebrow:"Cocobella with John Lewis", pat:"pat-sofas",
   lede:"Our collaboration with John Lewis. Beautiful, comfortable sofas at a friendlier price, with covers you can wash at home.",
   match:v=>v.range==="Cocobella (John Lewis)"},
  {slug:"beds", title:"The Big Sleep", eyebrow:"Beds & Mattresses with Dreams", pat:"pat-beds",
   lede:"Our beds and mattresses with Dreams, made for the best night of your life.",
   match:v=>v.cat==="Beds & Mattresses"},
  {slug:"home", title:"A Room with a View", eyebrow:"Home Tours & Lifestyle", pat:"pat-home",
   lede:"Home tours and quiet corners, including a proper look around Salthrop House.",
   match:v=>v.cat==="Home & Lifestyle"},
  {slug:"tableware", title:"Dinner at Eight", eyebrow:"Tableware & Candles with Portmeirion", pat:"pat-tableware",
   lede:"Twenty years of Portmeirion. Tableware, candles and tables worth lingering at.",
   match:v=>v.cat==="Tableware & Candles"},
  {slug:"recipes", title:"Breakfast at Sophie's", eyebrow:"Recipes & Cooking", pat:"pat-recipes",
   lede:"Sophie in the kitchen. Recipes and food made to be shared.",
   match:v=>v.cat==="Recipes & Cooking"},
  {slug:"bedlinen", title:"Between the Sheets", eyebrow:"Bed Linen with Welspun", pat:"pat-linen",
   lede:"Our bed linen with Welspun. Soft, honest cotton for beautiful mornings.",
   match:v=>v.cat==="Bed Linen"},
  {slug:"garden", title:"The Secret Garden", eyebrow:"Gardening with Burgon & Ball", pat:"pat-garden",
   lede:"Out in the garden with Burgon & Ball.",
   match:v=>v.cat==="Gardening"},
  {slug:"sofas", title:"The Magnificent Seven", eyebrow:"The Sofa Family", pat:"pat-sofas",
   lede:"Barton, Henrietta, Sophia, Isabel and the rest of the sofa family, plus footstools, ottomans and coffee tables.",
   match:v=>v.cat==="Sofas & Furniture"&&!["Coco","Coco Corner","Cocobella (John Lewis)"].includes(v.range)},
];

var FACETS = [
  {key:"range", label:"Range"},
  {key:"type", label:"Film type"},
  {key:"durB", label:"Length", order:["Under 30 seconds","30 to 60 seconds","1 to 3 minutes","Over 3 minutes"]},
  {key:"stage", label:"Journey stage"},
  {key:"orient", label:"Format"},
  {key:"sound", label:"Sound"},
  {key:"tone", label:"Tone"}
];

/* ------- helpers ------- */
var $ = (s,root=document)=>root.querySelector(s);
var esc = s=>(s||"").replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;").replace(/"/g,"&quot;");
var isPortrait = o=>o && o.startsWith("Portrait");

/* Turn a pasted Vimeo link into an id (and privacy hash for unlisted links).
   Accepts: https://vimeo.com/123456789
            https://vimeo.com/123456789/abcdef1234   (unlisted)
            https://vimeo.com/123456789?h=abcdef1234
            https://player.vimeo.com/video/123456789
            or just the bare number. */
function parseVimeo(link){
  var s=String(link||"").trim();
  if(!s) return {id:"",hash:""};
  if(/^\d+$/.test(s)) return {id:s,hash:""};
  var m=s.match(/(?:vimeo\.com\/(?:video\/)?)(\d+)(?:\/([0-9a-zA-Z]+))?/);
  var id=m?m[1]:"", hash=(m&&m[2])||"";
  var h=s.match(/[?&]h=([0-9a-zA-Z]+)/);
  if(h) hash=h[1];
  return {id:id,hash:hash};
}

function fmtDur(s){
  s=Math.round(Number(s)||0);
  if(!s) return "";
  var m=Math.floor(s/60), r=s%60;
  return m+":"+(r<10?"0":"")+r;
}
function durBucket(s){
  s=Number(s)||0;
  if(!s) return "";
  if(s<30) return "Under 30 seconds";
  if(s<=60) return "30 to 60 seconds";
  if(s<=180) return "1 to 3 minutes";
  return "Over 3 minutes";
}

/* Fill in the derived fields the UI relies on. Called once at boot. */
function normalise(v){
  var pv=parseVimeo(v.vimeo||v.vimeoId);
  v.vimeoId=v.vimeoId||pv.id;
  v.vimeoHash=v.vimeoHash||pv.hash;
  v.durS=Number(v.durS)||0;
  v.dur=v.dur||fmtDur(v.durS);
  v.durB=v.durB||durBucket(v.durS);
  v.id=v.id||v.handle||("film_"+v.vimeoId);
  v.handle=v.handle||v.id;
  v.isNew=(v.isNew===true||v.isNew==="true");
  ["desc","range","fabric","type","orient","sound","tone","stage","cat"].forEach(function(k){ if(v[k]==null) v[k]=""; });
  return v;
}

function findFilm(ref){
  if(!ref) return null;
  return DATA.find(function(x){ return x.id===ref||x.handle===ref; })||null;
}

function thumbHTML(v, overrideImage, extra){
  const flag = isPortrait(v.orient) ? '<span class="flag">Portrait</span>' : "";
  const playBtn = '<span class="play"><svg width="18" height="18" viewBox="0 0 24 24" fill="#3B5679"><path d="M8 5v14l11-7z"/></svg></span>';
  const durBadge = v.dur ? `<span class="dur">${v.dur}</span>` : "";
  const extraHTML = extra || "";
  const src = overrideImage || v.poster;

  if(src){
    return `<span class="thumb thumb-real" aria-hidden="true">
      <img class="thumb-img" src="${esc(src)}" alt="" loading="lazy" decoding="async"
           onload="this.closest('.thumb').classList.add('thumb-loaded')"
           onerror="this.closest('.thumb').classList.add('thumb-error')">
      ${flag}${playBtn}${durBadge}${extraHTML}
    </span>`;
  }

  /* No thumbnail yet (it may still be loading from Vimeo): show the pattern. */
  return `<span class="thumb ${PAT[v.cat]||"pat-brand"}" aria-hidden="true" data-thumb-for="${esc(v.id)}">
    <span class="mono">SC</span>${flag}${playBtn}${durBadge}${extraHTML}
  </span>`;
}

/* ======= reusable library component (home + collection views) ======= */
function Library(rootEl, opts){
  if(!rootEl) return null;
  const state = { cat:"All", q:"", sort:"featured", sel:{}, shown:24, scope:opts.scope||(()=>true), showTabs:!!opts.showTabs };
  FACETS.forEach(f=>state.sel[f.key]=new Set());
  const el = {
    tabs:$("[data-tabs]",rootEl), q:$("[data-q]",rootEl), ddrow:$("[data-ddrow]",rootEl),
    sort:$("[data-sort]",rootEl), status:$("[data-status]",rootEl), grid:$("[data-grid]",rootEl),
    empty:$("[data-empty]",rootEl), lmw:$("[data-loadmorewrap]",rootEl)
  };
  const scoped=()=>DATA.filter(state.scope);
  function baseSet(){
    return scoped().filter(v=>{
      if(state.cat!=="All" && v.cat!==state.cat) return false;
      if(state.q){
        const hay=(v.title+" "+v.desc+" "+v.range+" "+v.fabric+" "+v.type).toLowerCase();
        if(!hay.includes(state.q)) return false;
      }
      return true;
    });
  }
  function facetMatch(v,skip){
    return FACETS.every(f=>{
      if(f.key===skip) return true;
      const sel=state.sel[f.key];
      return sel.size===0||sel.has(v[f.key]);
    });
  }
  function filtered(){
    let out=baseSet().filter(v=>facetMatch(v,null));
    const fRank=v=>{let i=FEATURED_IDS.indexOf(v.id); if(i<0) i=FEATURED_IDS.indexOf(v.handle); return i<0?99:i;};
    switch(state.sort){
      case "short": out.sort((a,b)=>a.durS-b.durS); break;
      case "long": out.sort((a,b)=>b.durS-a.durS); break;
      case "az": out.sort((a,b)=>a.title.localeCompare(b.title)); break;
      case "range": out.sort((a,b)=>(a.range||"zz").localeCompare(b.range||"zz")||a.title.localeCompare(b.title)); break;
      default: out.sort((a,b)=>fRank(a)-fRank(b)||a.title.localeCompare(b.title));
    }
    return out;
  }
  function renderTabs(){
    if(!state.showTabs){ el.tabs.innerHTML=""; return; }
    const counts={}; scoped().forEach(v=>counts[v.cat]=(counts[v.cat]||0)+1);
    const cats=Object.keys(CAT_LABEL).filter(c=>counts[c]);
    el.tabs.innerHTML=[`<button class="tab ${state.cat==="All"?"on":""}" data-cat="All">All films <span class="n">${scoped().length}</span></button>`]
      .concat(cats.map(c=>`<button class="tab ${state.cat===c?"on":""}" data-cat="${esc(c)}">${esc(CAT_LABEL[c])} <span class="n">${counts[c]}</span></button>`)).join("");
  }
  function renderDropdowns(){
    const base=baseSet();
    el.ddrow.innerHTML=FACETS.map(f=>{
      const pool=base.filter(v=>facetMatch(v,f.key));
      const counts={}; pool.forEach(v=>{const val=v[f.key]; if(val)counts[val]=(counts[val]||0)+1;});
      let vals=Object.keys(counts);
      if(f.order) vals=f.order.filter(x=>counts[x]); else vals.sort((a,b)=>counts[b]-counts[a]||a.localeCompare(b));
      if(vals.length<2&&state.sel[f.key].size===0) return "";
      const n=state.sel[f.key].size;
      return `<div class="dd" data-facet="${f.key}">
        <button class="dd-btn ${n?"active":""}" aria-expanded="false">${f.label}${n?` <span class="badge">${n}</span>`:""}
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><path d="m6 9 6 6 6-6"/></svg>
        </button>
        <div class="dd-menu">
          ${vals.map(v=>`<label><input type="checkbox" value="${esc(v)}" ${state.sel[f.key].has(v)?"checked":""}> ${esc(v)} <span class="cnt">${counts[v]}</span></label>`).join("")}
        </div>
      </div>`;
    }).join("");
  }
  function renderStatus(total){
    const chips=[];
    if(state.cat!=="All") chips.push({t:CAT_LABEL[state.cat],k:"cat",v:state.cat});
    FACETS.forEach(f=>state.sel[f.key].forEach(v=>chips.push({t:v,k:f.key,v})));
    if(state.q) chips.push({t:`"${state.q}"`,k:"q",v:state.q});
    el.status.innerHTML=chips.map(c=>`<span class="chip">${esc(c.t)}<button data-chip-k="${esc(c.k)}" data-chip-v="${esc(c.v)}" aria-label="Remove ${esc(c.t)}">&#10005;</button></span>`).join("")
      +(chips.length?`<button class="clearall" data-clearall>Clear all</button>`:"")
      +`<span class="resultcount">${total} film${total===1?"":"s"}</span>`;
  }
  function renderGrid(){
    const out=filtered();
    renderStatus(out.length);
    el.grid.innerHTML=out.slice(0,state.shown).map(v=>`
      <button class="card" data-open="${esc(v.id)}">
        ${thumbHTML(v)}
        <h3>${esc(v.title)}</h3>
        <span class="meta"><b>${esc(v.range||CAT_LABEL[v.cat]||"")}</b>${v.type?` &nbsp;·&nbsp; ${esc(v.type)}`:""}${v.dur?` &nbsp;·&nbsp; ${v.dur}`:""}</span>
      </button>`).join("");
    el.empty.style.display=out.length?"none":"block";
    el.lmw.style.display=out.length>state.shown?"flex":"none";
  }
  function renderAll(){ renderTabs(); renderDropdowns(); renderGrid(); }
  function reset(){
    state.cat="All"; state.q=""; el.q.value=""; state.shown=24;
    FACETS.forEach(f=>state.sel[f.key].clear());
  }

  rootEl.addEventListener("click",e=>{
    const tab=e.target.closest(".tab");
    if(tab){ state.cat=tab.dataset.cat; state.shown=24; renderAll(); return; }
    const ddBtn=e.target.closest(".dd-btn");
    if(ddBtn){
      const dd=ddBtn.closest(".dd"); const was=dd.classList.contains("open");
      rootEl.querySelectorAll(".dd.open").forEach(d=>d.classList.remove("open"));
      if(!was) dd.classList.add("open");
      return;
    }
    if(!e.target.closest(".dd-menu")) rootEl.querySelectorAll(".dd.open").forEach(d=>d.classList.remove("open"));
    const chipX=e.target.closest("[data-chip-k]");
    if(chipX){
      const {chipK,chipV}=chipX.dataset;
      if(chipK==="cat") state.cat="All";
      else if(chipK==="q"){ state.q=""; el.q.value=""; }
      else state.sel[chipK].delete(chipV);
      state.shown=24; renderAll(); return;
    }
    if(e.target.closest("[data-clearall]")||e.target.closest("[data-emptyclear]")){ reset(); renderAll(); return; }
    if(e.target.closest("[data-loadmore]")){ state.shown+=24; renderGrid(); }
  });
  rootEl.addEventListener("change",e=>{
    const cb=e.target.closest(".dd-menu input");
    if(cb){
      const key=cb.closest(".dd").dataset.facet;
      cb.checked?state.sel[key].add(cb.value):state.sel[key].delete(cb.value);
      state.shown=24;
      renderDropdowns(); renderGrid();
      const dd=rootEl.querySelector(`.dd[data-facet="${key}"]`);
      if(dd) dd.classList.add("open");
    }
    if(e.target.matches("[data-sort]")){ state.sort=e.target.value; renderGrid(); }
  });
  let qt;
  el.q.addEventListener("input",e=>{
    clearTimeout(qt);
    qt=setTimeout(()=>{ state.q=e.target.value.trim().toLowerCase(); state.shown=24; renderAll(); },160);
  });

  return {
    renderAll,
    renderGrid,
    reset,
    setScope(fn){ state.scope=fn; reset(); renderAll(); },
    /* Apply URL-based filter presets. Reads key=value pairs from the
       hash and maps them to category tabs, search, or faceted filters.
       Called once at boot before the first render. */
    applyPresets(params){
      if(!params||!params.size) return;
      if(params.has("cat")){
        var catVal=params.get("cat");
        var found=Object.keys(CAT_LABEL).find(function(k){
          return k===catVal||CAT_LABEL[k]===catVal||k.toLowerCase().indexOf(catVal.toLowerCase())===0;
        });
        if(found) state.cat=found;
      }
      if(params.has("q")){
        state.q=params.get("q").toLowerCase();
        if(el.q) el.q.value=params.get("q");
      }
      FACETS.forEach(function(f){
        if(params.has(f.key)){
          params.get(f.key).split(",").forEach(function(v){
            if(v.trim()) state.sel[f.key].add(v.trim());
          });
        }
      });
    }
  };
}

/* ======= what's new (home) ======= */
/* Films with the "Show in What's New" box ticked in Shopify appear here.
   The whole section stays hidden when nothing is ticked. */
function renderWhatsNew(){
  var sec=document.getElementById("whatsNew");
  var row=document.getElementById("newRow");
  if(!sec||!row) return;
  var films=DATA.filter(function(v){ return v.isNew; });
  /* Newest first: each film's Shopify id grows with creation, so sort by it descending. */
  function seqNum(v){ var m=String(v.seq||"").match(/(\d+)\s*$/); return m?Number(m[1]):0; }
  films.sort(function(a,b){ return seqNum(b)-seqNum(a); });
  if(!films.length){ sec.hidden=true; row.innerHTML=""; return; }
  sec.hidden=false;
  row.innerHTML=films.map(function(v){
    return '<button class="card" data-open="'+esc(v.id)+'">'
      + thumbHTML(v, null, '<span class="newflag">New</span>')
      + '<h3>'+esc(v.title)+'</h3>'
      + '<span class="meta"><b>'+esc(v.range||CAT_LABEL[v.cat]||"")+'</b>'
      + (v.type?' &nbsp;·&nbsp; '+esc(v.type):"")
      + (v.dur?' &nbsp;·&nbsp; '+v.dur:"")
      + '</span></button>';
  }).join("");
}

/* ======= featured & collections (home) ======= */
function renderFeatured(){
  var grid=$("#featGrid");
  if(!grid) return;
  const main=findFilm(FEATURED_IDS[0]);
  const side=FEATURED_IDS.slice(1).map(findFilm).filter(Boolean);
  if(!main){ grid.innerHTML=""; return; }
  grid.innerHTML=`
    <button class="feat-main" data-open="${esc(main.id)}">
      ${thumbHTML(main, FEATURED_IMAGES[0])}
      <span class="body"><h3>${esc(main.title)}</h3><p>${esc(FEATURED_BLURB[main.id]||main.desc)}</p></span>
    </button>
    <div class="feat-side">
      ${side.map((v,i)=>`
        <button class="feat-card" data-open="${esc(v.id)}">
          ${thumbHTML(v, FEATURED_IMAGES[i+1])}
          <span class="body"><h3>${esc(v.title)}</h3><span class="meta">${esc(v.range||CAT_LABEL[v.cat]||"")}${v.type?` &nbsp;·&nbsp; ${esc(v.type)}`:""}</span></span>
        </button>`).join("")}
    </div>`;
}
function renderCollections(){
  var grid=$("#collGrid");
  if(!grid) return;
  grid.innerHTML=COLLECTIONS.map(c=>{
    const n=DATA.filter(c.match).length;
    if(!n) return "";
    return `<button class="coll-card" data-coll="${c.slug}">
      <span class="swatch ${c.pat}"></span>
      <span class="body">
        <span class="now">${esc(c.eyebrow)}</span>
        <h3>${esc(c.title)}</h3>
        <p>${esc(c.lede)}</p>
        <span class="n">${n} films &rarr;</span>
      </span>
    </button>`;
  }).join("");
}

/* ======= thumbnails from Vimeo (for films with no stored thumbnail) ======= */
/* New films added in Shopify won't have a thumbnail URL yet. Ask Vimeo's
   public oEmbed endpoint for one, then refresh any cards on screen. This
   works for public and unlisted films (the privacy hash is included). */
function loadMissingPosters(onDone){
  var missing=DATA.filter(function(v){ return !v.poster && v.vimeoId; });
  if(!missing.length||typeof fetch!=="function"){ if(onDone) onDone(); return; }
  var pending=missing.length;
  missing.forEach(function(v){
    var url="https://vimeo.com/"+v.vimeoId+(v.vimeoHash?"/"+v.vimeoHash:"");
    fetch("https://vimeo.com/api/oembed.json?width=640&url="+encodeURIComponent(url))
      .then(function(r){ return r.ok?r.json():null; })
      .then(function(j){
        if(j&&j.thumbnail_url) v.poster=j.thumbnail_url;
        if(j&&j.duration&&!v.durS){ v.durS=j.duration; v.dur=fmtDur(j.duration); v.durB=durBucket(j.duration); }
        if(j&&j.width&&j.height&&!v.orient){
          var r=j.width/j.height;
          v.orient=r<0.7?"Portrait 9:16":r<0.95?"Portrait 4:5":r<1.15?"Near square":"Landscape 16:9";
        }
      })
      .catch(function(){})
      .then(function(){ if(--pending===0&&onDone) onDone(); });
  });
}

/* ======= modal ======= */
function slugify(s){
  return String(s).toLowerCase().replace(/&/g,'and').replace(/[^a-z0-9]+/g,'-').replace(/^-+|-+$/g,'').substring(0,60);
}
var _modalOpen=false;

function openModal(id, skipHash){
  const v=findFilm(id); if(!v) return;
  _modalOpen=true;
  $("#mTitle").textContent=v.title;
  $("#mDesc").textContent=v.desc;
  const tags=[["warm",v.range||CAT_LABEL[v.cat]],["",v.type],["",v.dur],["",v.orient],["",v.sound]];
  if(v.fabric) tags.push(["",v.fabric]);
  $("#mTags").innerHTML=tags.filter(t=>t[1]).map(t=>`<span class="tag ${t[0]}">${esc(t[1])}</span>`).join("")+
    '<button class="copy-link" id="mCopyLink" type="button" aria-label="Share this film">'+
      '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M4 12v7a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/></svg>'+
      '<span>Share</span>'+
    '</button>';

  var sheet=document.querySelector("#modal .sheet");
  if(sheet){
    sheet.classList.remove("orient-portrait","orient-square");
    if(isPortrait(v.orient)&&v.orient!=="Portrait 4:5") sheet.classList.add("orient-portrait");
    else if(v.orient==="Near square"||v.orient==="Portrait 4:5") sheet.classList.add("orient-square");
  }

  var playerEl=$("#mPlayer");
  if(playerEl){
    if(v.vimeoId){
      var embedParams='autoplay=1&badge=0&autopause=0&transparent=0';
      if(v.vimeoHash) embedParams+='&h='+esc(v.vimeoHash);
      playerEl.innerHTML='<iframe src="https://player.vimeo.com/video/'+esc(v.vimeoId)+
        '?'+embedParams+'" frameborder="0" '+
        'allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media" '+
        'allowfullscreen title="'+esc(v.title)+'" style="position:absolute;inset:0;width:100%;height:100%"></iframe>';
      playerEl.style.position='relative';
    } else {
      playerEl.innerHTML='<div class="pill">Vimeo player</div>'+
        '<div class="ref">This film has no Vimeo link yet.</div>';
      playerEl.style.position='';
    }
  }

  if(!skipHash && history.replaceState){
    history.replaceState(null,'',location.pathname+'#/film/'+v.id+'/'+slugify(v.title));
  }

  var copyBtn=$("#mCopyLink");
  if(copyBtn){
    var newBtn=copyBtn.cloneNode(true);
    copyBtn.parentNode.replaceChild(newBtn,copyBtn);
    newBtn.addEventListener("click",function(){
      var url=location.origin+location.pathname+'#/film/'+v.id+'/'+slugify(v.title);
      function showCopied(){
        newBtn.querySelector("span").textContent="Copied!";
        newBtn.querySelector("svg").style.display="none";
        setTimeout(function(){
          newBtn.querySelector("span").textContent="Share";
          newBtn.querySelector("svg").style.display="";
        },2000);
      }
      if(navigator.clipboard&&navigator.clipboard.writeText){
        navigator.clipboard.writeText(url).then(showCopied);
      } else {
        var ta=document.createElement("textarea");
        ta.value=url; ta.style.position="fixed"; ta.style.opacity="0";
        document.body.appendChild(ta); ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        showCopied();
      }
    });
  }

  $("#modal").classList.add("open");
  document.body.style.overflow="hidden";
}
function closeModal(){
  var playerEl=$("#mPlayer");
  if(playerEl) playerEl.innerHTML='';
  $("#modal").classList.remove("open");
  document.body.style.overflow="";
  _modalOpen=false;
  if(history.replaceState && location.hash.indexOf('#/film/')===0){
    history.replaceState(null,'',location.pathname+(location.hash.match(/^#\/(collection\/.*)/) ? '#/'+RegExp.$1 : ''));
  }
}
document.addEventListener("click",e=>{
  const b=e.target.closest("[data-open]"); if(b){ openModal(b.dataset.open); return; }
  if(e.target.closest("[data-close]")) closeModal();
  const cc=e.target.closest("[data-coll]"); if(cc){ location.hash="#/collection/"+cc.dataset.coll; }
});
document.addEventListener("keydown",e=>{ if(e.key==="Escape") closeModal(); });

/* ======= film data ======= */
/* The section writes films into <script type="application/json" data-sc-film-data>,
   one page of up to 250 at a time (Shopify's per-page limit). Page 1 arrives with the
   page itself. Any further pages are fetched by requesting the section again through
   Shopify's Section Rendering API (this URL + ?section_id=...&page=N), then merged. */
function readFilmPage(doc){
  var el=doc&&doc.querySelector("[data-sc-film-data]");
  if(!el) return null;
  try { return JSON.parse(el.textContent); } catch(e){ return null; }
}

function loadFilms(onDone){
  var first=readFilmPage(document);
  /* No data block (e.g. the older section file is still live): use SC.filmRoom.DATA. */
  if(!first){ onDone(SC.filmRoom.DATA||[]); return; }
  var others=[];
  for(var i=1;i<=first.pages;i++){ if(i!==first.page) others.push(i); }
  if(!others.length||!first.section||typeof fetch!=="function"){ onDone(first.films||[]); return; }
  Promise.all(others.map(function(n){
    var url=new URL(location.href);
    url.searchParams.set("section_id",first.section);
    url.searchParams.set(first.param||"page",n);
    return fetch(url)
      .then(function(r){ return r.ok?r.text():""; })
      .then(function(html){ return readFilmPage(new DOMParser().parseFromString(html,"text/html")); })
      .catch(function(){ return null; });
  })).then(function(pages){
    /* A page that fails to load is skipped rather than blanking the library. Films are
       keyed by handle so none appear twice if the list changed between requests. */
    var films=[], seen={};
    [first].concat(pages).forEach(function(p){
      ((p&&p.films)||[]).forEach(function(f){
        var key=f.handle||f.id;
        if(key&&seen[key]) return;
        seen[key]=true;
        films.push(f);
      });
    });
    onDone(films);
  });
}

/* ======= boot ======= */
function boot(films){
  /* Show every Active film that has a usable Vimeo link and suits the visitor's region.
     Region is set per film in Shopify (All regions / UK only / US only). Visitors outside
     the UK and US see only "All regions" films. */
  var country=(SC.filmRoom.COUNTRY||"").toUpperCase();
  function regionOk(v){
    var r=(v.region||"All regions");
    if(r==="UK only") return country==="GB";
    if(r==="US only") return country==="US";
    return true;
  }
  DATA=(films||[]).map(normalise).filter(function(v){ return !!v.vimeoId && regionOk(v); });

  var viewHome=$("#view-home");
  var viewColl=$("#view-coll");
  var libHome=viewHome?Library(viewHome,{showTabs:true}):null;
  var libColl=viewColl?Library(viewColl,{showTabs:false}):null;

  function route(){
    var filmMatch=location.hash.match(/^#\/film\/([\w-]+)/);
    if(filmMatch){
      var v=findFilm(filmMatch[1]);
      if(v){ openModal(v.id, true); return; }
    }
    if(_modalOpen) closeModal();

    if(!viewColl) return;
    var collMatch=location.hash.match(/^#\/collection\/([\w-]+)/);
    var coll=collMatch&&COLLECTIONS.find(function(c){ return c.slug===collMatch[1]; });
    if(coll){
      viewHome.classList.remove("on");
      viewColl.classList.add("on");
      $("#collCrumb").textContent=coll.title;
      $("#collEyebrow").textContent=coll.eyebrow;
      $("#collTitle").textContent=coll.title;
      $("#collLede").textContent=coll.lede;
      $("#collCount").textContent=DATA.filter(coll.match).length;
      if(libColl) libColl.setScope(coll.match);
      window.scrollTo(0,0);
    } else {
      viewColl.classList.remove("on");
      viewHome.classList.add("on");
    }
  }
  window.addEventListener("hashchange",route);

  var countEl=$("#totalCount");
  if(countEl) countEl.textContent=DATA.length;
  renderWhatsNew();
  renderFeatured();
  renderCollections();

  var filterMatch=location.hash.match(/^#\/\?(.+)/);
  if(filterMatch && libHome){
    try {
      var params=new URLSearchParams(filterMatch[1]);
      libHome.applyPresets(params);
    } catch(e){}
  }

  if(libHome) libHome.renderAll();
  route();

  /* Fetch thumbnails for any films that don't have one yet, then redraw. */
  loadMissingPosters(function(){
    renderWhatsNew();
    renderFeatured();
    if(libHome) libHome.renderGrid();
    if(libColl) libColl.renderGrid();
  });
}
function start(){ loadFilms(boot); }
if(document.readyState==="loading") document.addEventListener("DOMContentLoaded",start);
else start();

})(typeof window !== 'undefined' ? window : this);
